import { normalizeBlank, type PredictAnswer } from '../judge';
import { shuffleById } from './shuffle';
import type { CardType, MemoCard, ParsonsCard, PredictOutputCard } from './types';

/** predict-output に常に足す 2 択の表示（card-schema.md §3.1、content-conventions.md §4.3）。 */
export const COMPILE_ERROR_LABEL = 'コンパイルエラー';
export const RUNTIME_ERROR_LABEL = '実行時エラー';

export interface PredictChoice {
  /** 表示する文字列。 */
  label: string;
  /** judge の gradePredictOutput に渡す回答。 */
  answer: PredictAnswer;
}

/**
 * predict-output の選択肢（card-schema.md §3.1）。YAML の choices に「コンパイルエラー」「実行時エラー」を必ず足す。
 * choices が無い（自由入力）カードでも 2 択は返す。出力候補は id を種にシャッフルし、2 択は末尾に置く。
 */
export function predictOutputChoices(
  card: Pick<PredictOutputCard, 'id' | 'choices'>,
): PredictChoice[] {
  const outputs = shuffleById(card.id, card.choices ?? []).map(({ item }): PredictChoice => ({
    label: item,
    answer: { kind: 'output', text: item },
  }));
  return [
    ...outputs,
    { label: COMPILE_ERROR_LABEL, answer: { kind: 'compile-error' } },
    { label: RUNTIME_ERROR_LABEL, answer: { kind: 'runtime-error' } },
  ];
}

/** parsons の出題時の並び（正解の lines と distractors を混ぜ、正解の並びそのものにはしない）。 */
export function parsonsPool(card: Pick<ParsonsCard, 'id' | 'lines' | 'distractors'>): string[] {
  const all = [...card.lines, ...(card.distractors ?? [])];
  return shuffleById(card.id, all, true).map(({ item }) => item);
}

/** memo の想起（card-schema.md §3.9）の画面の文言。2 つの自己申告ボタンは同じ見た目にする。 */
export const MEMO_REVEAL_LABEL = '答えを見る';
export const MEMO_RECALLED_LABEL = '思い出せた';
export const MEMO_FORGOT_LABEL = '思い出せなかった';

export type MemoRecallMode =
  /** recall.accept なし。答えを見て「思い出せた / 思い出せなかった」を選ぶ。 */
  | { kind: 'self-report' }
  /** recall.accept あり。入力を fill-blank と同じ規則で照合する（match の既定は trim）。 */
  | { kind: 'typed'; accept: readonly string[]; match: 'trim' | 'ignore-spaces' };

export function memoRecallMode(card: Pick<MemoCard, 'recall'>): MemoRecallMode {
  const { accept, match } = card.recall;
  return accept && accept.length > 0
    ? { kind: 'typed', accept, match: match ?? 'trim' }
    : { kind: 'self-report' };
}

/**
 * memo の回答を Leitner の結果にする（§3.9 の表）。memo にヒントは無いので hint は常に false。
 * - self-report: recalled（思い出せた）なら correct
 * - typed: 入力が許容解のどれかと一致すれば correct。入力前に「答えを見る」を押したら revealed で wrong
 */
export type MemoAnswer =
  | { kind: 'self-report'; recalled: boolean }
  | { kind: 'typed'; input: string }
  | { kind: 'revealed' };

export function gradeMemo(card: Pick<MemoCard, 'recall'>, answer: MemoAnswer): 'correct' | 'wrong' {
  const mode = memoRecallMode(card);
  if (answer.kind === 'revealed') return 'wrong';
  if (answer.kind === 'self-report') return answer.recalled ? 'correct' : 'wrong';
  if (mode.kind !== 'typed') return 'wrong';
  const got = normalizeBlank(answer.input, mode.match);
  return mode.accept.some((a) => normalizeBlank(a, mode.match) === got) ? 'correct' : 'wrong';
}

/** カード種別の表示名（画面の種別バッジ）。 */
export const CARD_TYPE_LABELS: Readonly<Record<CardType, string>> = {
  'predict-output': '出力予想',
  'fill-blank': '穴埋め',
  parsons: '並べ替え',
  choice: '選択',
  reduce: '帰着',
  'complexity-quiz': '計算量',
  'write-ac': '書いて AC',
  'viz-step': '可視化',
  memo: '暗記',
};
